import React, { useState, useEffect } from 'react';
import { FiUser, FiUsers, FiBriefcase, FiServer, FiChevronDown, FiFolder, FiFileText } from 'react-icons/fi';

interface CustomerFolder {
  id: string;
  name: string;
  createdAt: string;
}

interface Engagement {
  id: string;
  name: string;
  description: string;
  primaryOwner: string;
  secondaryOwners: string[];
  createdAt: string;
}

type PersonaSelectorWithContextProps = {
  onSelect: (persona: string) => void;
  onContextSelect: (customerId?: string, customerName?: string, engagementId?: string, engagementName?: string) => void;
  selectedPersona: string;
  onStartChat?: () => void;
};

const PersonaSelectorWithContext: React.FC<PersonaSelectorWithContextProps> = ({
  onSelect,
  onContextSelect,
  selectedPersona,
  onStartChat,
}) => {
  const [customers, setCustomers] = useState<CustomerFolder[]>([]);
  const [engagements, setEngagements] = useState<{ [customerId: string]: Engagement[] }>({});
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedEngagementId, setSelectedEngagementId] = useState<string>('');

  // Load customers from localStorage
  useEffect(() => {
    const savedCustomers = localStorage.getItem('qnav-customers');
    if (savedCustomers) {
      const parsedCustomers = JSON.parse(savedCustomers);
      setCustomers(parsedCustomers);

      // Load engagements for each customer
      const engagementsMap: { [customerId: string]: Engagement[] } = {};
      parsedCustomers.forEach((customer: CustomerFolder) => {
        const savedEngagements = localStorage.getItem(`qnav-engagements-${customer.id}`);
        if (savedEngagements) {
          engagementsMap[customer.id] = JSON.parse(savedEngagements);
        } else {
          engagementsMap[customer.id] = [];
        }
      });
      setEngagements(engagementsMap);
    }
  }, []);

  const personas = [
    {
      id: 'csm',
      name: 'Account CSM',
      description: 'Strategic guidance and oversight of customer success outcomes',
      icon: <FiBriefcase className="h-6 w-6" />,
    },
    {
      id: 'advisor',
      name: 'Customer Success Specialist',
      description: 'Prescriptive guidance and resources for transformation delivery',
      icon: <FiUser className="h-6 w-6" />,
      disabled: true,
    },
    {
      id: 'tam',
      name: 'TAM/SA (Technical)',
      description: 'Service guidance, use case optimization, and best practices ',
      icon: <FiServer className="h-6 w-6" />,
      disabled: true,
    },
    {
      id: 'partner',
      name: 'Partner',
      description: 'Enablement tools and frameworks for partner-led delivery',
      icon: <FiUsers className="h-6 w-6" />,
      disabled: true,
    },
  ];

  const handlePersonaSelect = (persona: string) => {
    // Just update the selected persona
    onSelect(persona);

    // Reset selections when changing persona
    setSelectedCustomerId('');
    setSelectedEngagementId('');
    onContextSelect(undefined, undefined, undefined, undefined);
  };

  const handleCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const customerId = e.target.value;
    setSelectedCustomerId(customerId);
    setSelectedEngagementId(''); // Reset engagement when customer changes

    if (customerId) {
      const customer = customers.find((c) => c.id === customerId);
      if (customer) {
        onContextSelect(customerId, customer.name, undefined, undefined);
      }
    } else {
      onContextSelect(undefined, undefined, undefined, undefined);
    }
  };

  const handleEngagementChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const engagementId = e.target.value;
    setSelectedEngagementId(engagementId);

    if (engagementId && selectedCustomerId) {
      const customer = customers.find((c) => c.id === selectedCustomerId);
      const engagement = engagements[selectedCustomerId]?.find((e) => e.id === engagementId);

      if (customer && engagement) {
        onContextSelect(selectedCustomerId, customer.name, engagementId, engagement.name);
      }
    } else if (selectedCustomerId) {
      const customer = customers.find((c) => c.id === selectedCustomerId);
      if (customer) {
        onContextSelect(selectedCustomerId, customer.name, undefined, undefined);
      }
    } else {
      onContextSelect(undefined, undefined, undefined, undefined);
    }
  };

  return (
    <div className="persona-selector">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {personas.map((persona) => (
          <div
            key={persona.id}
            className={`persona-option p-4 border-2 rounded-qnav cursor-pointer transition-all ${
              selectedPersona === persona.id
                ? 'border-primary-600 bg-primary-50'
                : 'border-secondary-200 hover:border-primary-300 hover:bg-secondary-50'
            } ${persona.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={() => !persona.disabled && handlePersonaSelect(persona.id)}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className={`p-3 rounded-full mb-3 ${
                  selectedPersona === persona.id
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-secondary-100 text-secondary-600'
                } ${persona.disabled ? 'bg-secondary-200' : ''}`}
              >
                {persona.icon}
              </div>
              <h4 className="text-lg font-medium mb-1">
                {persona.name}
                {persona.disabled && <span className="text-xs ml-1 text-secondary-500">(Coming Soon)</span>}
              </h4>
              <div className="text-sm text-primary-600 font-medium mb-2">Persona</div>
              <p className="text-sm text-secondary-600">{persona.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Context dropdowns - only show after persona is selected */}
      {selectedPersona && (
        <div
          className="mt-4 flex flex-wrap items-center gap-4 bg-secondary-50 p-3 rounded-lg border border-secondary-100 transition-all duration-300 animate-fadeIn"
          style={{ display: 'none' }}
        >
          <div className="text-sm text-secondary-600 font-medium">Optional Context:</div>

          {/* Customer dropdown */}
          <div className="flex-grow min-w-[200px]">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFolder className="text-primary-500" size={16} />
              </div>
              <select
                className="block w-full pl-10 pr-8 py-2 text-sm border border-secondary-200 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none bg-white shadow-sm appearance-none"
                value={selectedCustomerId}
                onChange={handleCustomerChange}
                style={{ backgroundPosition: 'right 0.5rem center' }}
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <FiChevronDown className="text-secondary-400" size={16} />
              </div>
            </div>
          </div>

          {/* Engagement dropdown - only enable if customer is selected */}
          <div className="flex-grow min-w-[200px]">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFileText className="text-secondary-500" size={16} />
              </div>
              <select
                className={`block w-full pl-10 pr-8 py-2 text-sm border border-secondary-200 rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none shadow-sm appearance-none ${
                  !selectedCustomerId ? 'bg-secondary-100 text-secondary-400' : 'bg-white'
                }`}
                value={selectedEngagementId}
                onChange={handleEngagementChange}
                disabled={!selectedCustomerId}
                style={{ backgroundPosition: 'right 0.5rem center' }}
              >
                <option value="">Select Engagement</option>
                {selectedCustomerId &&
                  engagements[selectedCustomerId]?.map((engagement) => (
                    <option key={engagement.id} value={engagement.id}>
                      {engagement.name}
                    </option>
                  ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <FiChevronDown className="text-secondary-400" size={16} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonaSelectorWithContext;
